import { useRouter } from "next/navigation";
import { useContext } from "react";
import { twMerge } from "tailwind-merge";

import { DailyDealBadge } from "@/components/Inventory/Badges/DailyDealBadge";
import { FlashSaleBadge } from "@/components/Inventory/Badges/FlashSaleBadge";
import { InventoryTypeBadge } from "@/components/Inventory/Badges/InventoryTypeBadge";
import { LayoutContext } from "@/contexts/LayoutContext";
import { useIsFavorite } from "@/hooks/useIsFavorite";
import { useShareInventory } from "@/hooks/useShareInventory";
import {
  getListPriceLabel,
  getUnlockPriceLabel,
  isNewUnit
} from "@/tools/inventory";
import { getFavoritesLabel } from "@/tools/views";
import { faHeart } from "@fortawesome/free-regular-svg-icons";
import {
  faArrowLeft,
  faChevronRight,
  faDownload,
  faHeart as faSolidHeart,
  faLocationDot,
  faShareNodes
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "@lamesarv-sdk/components";
import { getFacetCode } from "@lamesarv-sdk/tools";
import {
  IInventory,
  IInventoryPriceFlag,
  InventoryField,
  PropsWithClasses
} from "@lamesarv-sdk/types";

interface IShareButtonProps {
  item: IInventory;
}

const ShareButton = ({ item }: IShareButtonProps) => {
  const { canShare, handleShare } = useShareInventory(item);

  if (!canShare) return null;

  return (
    <button
      type="button"
      onClick={handleShare}
      className="flex flex-row gap-1 items-center justify-between border border-neutral-300 px-3 py-2 cursor-pointer leading-none group hover:bg-neutral-100"
      data-tag-group="inventory-header"
      data-tag="inventory-share"
    >
      <span className="text-xs uppercase group-hover:text-blue-500">Share</span>
      <div>
        <FontAwesomeIcon icon={faShareNodes} className="h-4 group-hover:text-blue-500" />
      </div>
    </button>
  );
};

interface DownloadButtonProps {
  label: string;
  url?: string | null;
}

const DownloadButton = ({ label, url }: DownloadButtonProps) => {
  if (!url) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-row gap-1 items-center justify-between border border-neutral-300 px-3 py-2 cursor-pointer leading-none group hover:bg-neutral-100"
      data-tag-group="inventory-header"
      data-tag={`inventory-download-${getFacetCode(label)}`}
      data-testid={`header-download-${label}`}
    >
      <span className="text-xs uppercase group-hover:text-blue-500">Download {label}</span>
      <div>
        <FontAwesomeIcon icon={faDownload} className="h-4 group-hover:text-blue-500" />
      </div>
    </a>
  );
};

interface FavoriteButtonProps {
  item: IInventory;
}

const FavoriteButton = ({ item }: FavoriteButtonProps) => {
  const { isFavorite, toggleFavorite } = useIsFavorite(item);

  return (
    <label
      className="flex flex-row gap-1 items-center justify-between border border-neutral-300 px-3 py-2 cursor-pointer leading-none group hover:bg-neutral-100"
      data-tag-group="inventory-header"
      data-tag="inventory-favorite"
    >
      <input type="checkbox" className="hidden" onChange={() => toggleFavorite()} checked={isFavorite} />
      <span className={twMerge('text-xs uppercase group-hover:text-red-500', isFavorite && 'text-red-500')}>
        Favorite
      </span>
      <FontAwesomeIcon
        icon={isFavorite ? faSolidHeart : faHeart}
        className={twMerge('h-4 group-hover:text-red-500', isFavorite && 'text-red-500')}
      />
    </label>
  );
};

interface FavoriteCountProps extends PropsWithClasses {
  item: IInventory;
}

const FavoriteCount = ({ item, className }: FavoriteCountProps) => {
  const favorites = Number(item[InventoryField.favorites]) || 0;

  return (
    favorites > 0 && (
      <div className={twMerge('text-white text-xs uppercase bg-red-500 py-1 px-2 rounded-md font-bold', className)}>
        {getFavoritesLabel(favorites, 'this unit')}
      </div>
    )
  );
};

const formatPrice = (price: number) => {
  const cents = price % 1;

  return price.toLocaleString(undefined, { minimumFractionDigits: cents !== 0 ? 2 : 0 });
};

interface IGenericPricingProps {
  secondaryPrice?: {
    label: string;
    value: number;
  };
  primaryPrice?: {
    label: string;
    value: number;
  };
  displaySave?: boolean;
  item: IInventory;
  showCTA?: boolean;
}

const GenericPricing = ({ secondaryPrice, primaryPrice, displaySave, item, showCTA }: IGenericPricingProps) => {
  const bigPrice = primaryPrice || secondaryPrice;
  const smallPrice = primaryPrice && secondaryPrice;

  return (
    <div className="flex gap-2 flex-col md:flex-row md:items-end md:gap-6 md:hidden">
      {smallPrice && (
        <span className="text-xl text-gray-500">
          <span className="uppercase" data-testid="unit-list-label">
            {smallPrice.label}{' '}
          </span>
          <span className="line-through" data-testid="unit-list-price">
            ${formatPrice(smallPrice.value)}
          </span>
        </span>
      )}
      {bigPrice && (
        <span
          className={twMerge(
            'font-bold text-4xl',
            !displaySave && 'line-through',
            !displaySave && !!smallPrice && 'text-[#9B0505]',
          )}
          data-testid="unit-sale-price"
        >
          ${formatPrice(bigPrice.value)}
        </span>
      )}
      {displaySave && smallPrice && bigPrice && bigPrice.value !== smallPrice.value && (
        <span className="text-[#76881D] font-bold text-2xl">
          Save ${formatPrice(smallPrice.value - bigPrice.value)} (
          {(((smallPrice.value - bigPrice.value) / smallPrice.value) * 100).toFixed(0)}% off)
        </span>
      )}

      {showCTA && (
        <a
          href="#pdpform"
          className="hidden md:block self-start lg:self-center cursor-pointer text-center text-sm font-bold shadow-sm hover:shadow-md text-opacity-90 text-white px-4 py-3 uppercase rounded-sm bg-[#1E1E1E] border border-transparent hover:border-black hover:bg-white hover:text-black"
          data-testid="header-btn"
        >
          {getUnlockPriceLabel(item).buttonLabel}
        </a>
      )}
    </div>
  );
};

const PreownedPricing = ({ item }: { item: IInventory }) => {
  const listPrice = item[InventoryField.priceList];
  const basePrice = item[InventoryField.price] === listPrice ? undefined : item[InventoryField.price];
  const savePrice = basePrice ? listPrice - basePrice : undefined;

  return (
    <>
      <GenericPricing
        item={item}
        showCTA
        displaySave
        primaryPrice={
          !!item[InventoryField.priceSale] && { label: 'SALE PRICE', value: item[InventoryField.priceSale] }
        }
        secondaryPrice={
          !!item[InventoryField.priceList] && { label: getListPriceLabel(item), value: item[InventoryField.priceList] }
        }
      />
      <div className="hidden md:flex flex-col gap-1 text-xl lg:flex-row lg:flex-wrap lg:gap-4">
        {!!listPrice && (
          <div className="flex flex-row items-center gap-1">
            <span className="uppercase text-gray-500">{getListPriceLabel(item)}</span>
            <span className="font-bold line-through" data-testid="header-list-price">
              ${formatPrice(listPrice)}
            </span>
          </div>
        )}
        {!!basePrice && (
          <div className="flex flex-row items-center gap-1">
            <span className="text-md uppercase text-gray-500">Sale Price</span>
            <span className="font-bold text-red-500" data-testid="header-sale-price">
              ${formatPrice(basePrice)}
            </span>
          </div>
        )}
        {!!savePrice && (
          <div className="flex flex-row items-center gap-1">
            <span className="uppercase text-gray-500">You Save</span>
            <span className="font-bold text-[#76881D]" data-testid="header-save-price">
              ${formatPrice(savePrice)}
            </span>
          </div>
        )}

        <a
          href="#pdpform"
          className="hidden md:block self-start lg:self-center cursor-pointer text-center text-sm font-bold shadow-sm hover:shadow-md text-opacity-90 text-white px-4 py-3 uppercase rounded-sm bg-[#1E1E1E] border border-transparent hover:border-black hover:bg-white hover:text-black"
          data-testid="header-btn"
        >
          {getUnlockPriceLabel(item).buttonLabel}
        </a>
      </div>
    </>
  );
};

const NewPricing = ({ item }: { item: IInventory }) => {
  const salePrice =
    item[InventoryField.priceFlag] === IInventoryPriceFlag.windowPrice ||
    item[InventoryField.priceFlag] === IInventoryPriceFlag.codedCost
      ? item[InventoryField.priceSale]
      : item[InventoryField.priceWas];

  const displaySave = item[InventoryField.priceFlag] === IInventoryPriceFlag.windowPrice;

  return (
    <>
      <GenericPricing
        showCTA={!!item[InventoryField.priceList] && item[InventoryField.priceFlag] !== IInventoryPriceFlag.windowPrice}
        item={item}
        displaySave={displaySave}
        primaryPrice={!!salePrice && { label: 'SALE PRICE', value: salePrice }}
        secondaryPrice={
          !!item[InventoryField.priceList] && { label: getListPriceLabel(item), value: item[InventoryField.priceList] }
        }
      />
      <div className="hidden md:flex flex-col gap-1 text-xl lg:flex-row lg:flex-wrap lg:gap-4">
        {!!item[InventoryField.priceList] && (
          <div className="flex flex-row items-center gap-1">
            <span className="uppercase text-gray-500">{getListPriceLabel(item)}</span>
            <span className="font-bold line-through" data-testid="header-list-price">
              ${formatPrice(item[InventoryField.priceList])}
            </span>
          </div>
        )}

        {!!salePrice && (
          <div className="flex flex-row items-center gap-1">
            <span className="text-md uppercase text-gray-500">Sale Price</span>
            <span
              className={twMerge(
                'font-bold text-red-500',
                item[InventoryField.priceFlag] !== IInventoryPriceFlag.windowPrice && 'line-through',
              )}
              data-testid="header-sale-price"
            >
              ${formatPrice(salePrice)}
            </span>
          </div>
        )}

        {!!item[InventoryField.priceList] &&
          !!salePrice &&
          item[InventoryField.priceFlag] === IInventoryPriceFlag.windowPrice && (
            <div className="flex flex-row items-center gap-1">
              <span className="uppercase text-gray-500">You Save</span>
              <span className="font-bold text-[#76881D]" data-testid="header-save-price">
                ${formatPrice(item[InventoryField.priceList] - salePrice)}
              </span>
            </div>
          )}

        {!!item[InventoryField.priceList] && item[InventoryField.priceFlag] !== IInventoryPriceFlag.windowPrice && (
          <a
            href="#pdpform"
            className="hidden md:block self-start lg:self-center cursor-pointer rounded-sm bg-[#1E1E1E] border border-transparent hover:border-black hover:bg-white hover:text-black px-4 py-3 text-center text-sm font-bold uppercase text-white text-opacity-90 shadow-sm hover:shadow-md"
            data-testid="header-btn"
          >
            {getUnlockPriceLabel(item).buttonLabel}
          </a>
        )}
      </div>
    </>
  );
};

interface IDetailsHeaderProps {
  item: IInventory;
}

export const DetailsHeader = ({ item }: IDetailsHeaderProps) => {
  const {
    context: { inventoryLocations: locations },
  } = useContext(LayoutContext);
  const router = useRouter();

  const location =
    item[InventoryField.location] &&
    locations.find((location) => location.aliases.includes(item[InventoryField.location].toLowerCase().trim()));

  const hasRebate =
    !!item[InventoryField.rebate]?.enddate &&
    Number(item[InventoryField.rebate].enddate) > Math.ceil(new Date().getTime() / 1000);

  return (
    <div className="container px-4 md:px-6 mx-auto py-4 order-3 md:order-1 mt-2 md:mt-0">
      {/* Breadcrumbs */}
      <div className="hidden md:flex flex-row gap-0.5 items-center text-xs text-neutral-500">
        <a className="hover:text-brown-800 hover:underline hover:underline-offset-4 decoration-brown-800" href="/">
          Home
        </a>
        <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
        <a
          className="hover:text-brown-800 hover:underline hover:underline-offset-4 decoration-brown-800"
          href="/search"
        >
          Search Results
        </a>
        <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
        <span>
          {item[InventoryField.modelYear]}, {item[InventoryField.make]}, {item[InventoryField.model]}
        </span>
      </div>
      {/* Back */}
      <button onClick={() => router.back()} type="button" className="flex flex-row gap-2 items-center py-4">
        <FontAwesomeIcon icon={faArrowLeft} className="h-4" />
        <span className="text-sm uppercase">Back to Results</span>
      </button>
      {/* Info */}
      <div className="flex flex-col gap-2">
        <FavoriteCount item={item} className="self-start" />
        <div className="text-2xl font-bold uppercase" data-testid="header-unit-title">
          {item[InventoryField.title]}
        </div>
        <div className="flex flex-col xs:flex-row xs:items-center xs:flex-wrap gap-4 sm:gap-6 text-xs text-neutral-500">
          <div className="flex gap-3 items-center flex-wrap order-2">
            <InventoryTypeBadge inventoryType={item[InventoryField.inventoryType]} className="flex-shrink" />

            {item[InventoryField.isFlashSale] && <FlashSaleBadge />}

            {item[InventoryField.isDailyDeal] && <DailyDealBadge />}

            {hasRebate && (
              <Badge className="flex-shrink bg-red-600 text-white">
                ${item[InventoryField.rebate].amount.toLocaleString()} Rebate
              </Badge>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2.5 md:gap-6 order-1 md:order-3">
            {item[InventoryField.location] && (
              <a
                href={location ? `/${location.route}` : '#'}
                className="flex flex-row gap-1 items-center hover:text-brown-800 hover:underline hover:underline-offset-4 decoration-brown-800"
              >
                <FontAwesomeIcon icon={faLocationDot} className="h-4" />
                <span className="block" data-testid="header-location">
                  {item[InventoryField.location]}
                </span>
              </a>
            )}
            <div className="" data-testid="header-stock-number">
              Stock Number: {item[InventoryField.stockNumber]}
            </div>
          </div>
        </div>
        <div className="flex flex-row items-start md:items-center justify-between mt-2 md:mt-0">
          {isNewUnit(item) ? <NewPricing item={item} /> : <PreownedPricing item={item} />}
          <div className="hidden md:flex flex-col gap-2">
            <div className="flex flex-row gap-4 items-center">
              <div className="flex flex-row gap-4 items-center">
                <DownloadButton label="Full Brochure" url={item[InventoryField.brochureFileUrl]} />
                <DownloadButton label="MSRP" url={item[InventoryField.msrpFileUrl]} />
                <FavoriteButton item={item} />
              </div>
              <ShareButton item={item} />
            </div>
          </div>
        </div>
        <div className="md:hidden flex flex-col my-2 gap-2">
          <DownloadButton label="Brochure" url={item[InventoryField.brochureFileUrl]} />
          <DownloadButton label="MSRP" url={item[InventoryField.msrpFileUrl]} />
          <FavoriteButton item={item} />
          <ShareButton item={item} />
        </div>
      </div>
    </div>
  );
};
